import React, { useEffect, useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import './Hero.css'

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const { scrollY } = useScroll()
  
  // Parallax effects
  const backgroundY = useTransform(scrollY, [0, 500], [0, 150])
  const textY = useTransform(scrollY, [0, 500], [0, 100])
  const buttonY = useTransform(scrollY, [0, 500], [0, 50])

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      })
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <section id="home" className="hero">
      {/* Animated Background */}
      <motion.div 
        className="hero-background"
        style={{ y: backgroundY }}
      >
        <div className="hero-overlay" />
        <div className="hero-pattern" />
        
        {/* Floating Elements */}
        <motion.div
          className="floating-element element-1"
          animate={{
            x: mousePosition.x,
            y: mousePosition.y,
            rotate: 360
          }}
          transition={{
            x: { type: "spring", stiffness: 100, damping: 20 },
            y: { type: "spring", stiffness: 100, damping: 20 },
            rotate: { duration: 20, repeat: Infinity, ease: "linear" }
          }}
        />
        <motion.div
          className="floating-element element-2"
          animate={{
            x: mousePosition.x * -0.5,
            y: mousePosition.y * -0.5,
            rotate: -360
          }}
          transition={{
            x: { type: "spring", stiffness: 50, damping: 15 },
            y: { type: "spring", stiffness: 50, damping: 15 },
            rotate: { duration: 15, repeat: Infinity, ease: "linear" }
          }}
        />
        <motion.div
          className="floating-element element-3"
          animate={{
            x: mousePosition.x * 0.3,
            y: mousePosition.y * 0.8,
            scale: [1, 1.2, 1]
          }}
          transition={{
            x: { type: "spring", stiffness: 80, damping: 25 },
            y: { type: "spring", stiffness: 80, damping: 25 },
            scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
          }}
        />
      </motion.div>

      {/* Hero Content */}
      <div className="hero-content container">
        <motion.div
          className="hero-text"
          style={{ y: textY }}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Premium
            <motion.span 
              className="gradient-text"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              Cane Corso
            </motion.span>
            Puppies
          </motion.h1>
          
          <motion.p 
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            Discover the noble beauty and loyal companionship of our carefully bred Italian Mastiffs at
            norcalcanecorsopuppies.com. Each puppy is raised with love, proper socialization, and
            comprehensive health testing.
          </motion.p>
          
          <motion.div 
            className="hero-buttons"
            style={{ y: buttonY }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <Link to="/available-puppies">
              <motion.button
                className="btn btn-primary btn-lg"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <span>View Available Puppies</span>
                <motion.svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </motion.svg>
              </motion.button>
            </Link>
            
            <Link to="/about">
              <motion.button
                className="btn btn-ghost btn-lg"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                Learn More
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Stats Section */}
        <motion.div 
          className="hero-stats"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <motion.div 
            className="stat-item"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <span className="stat-number">15+</span>
            <span className="stat-label">Years Experience</span>
          </motion.div>
          <motion.div 
            className="stat-item"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <span className="stat-number">200+</span>
            <span className="stat-label">Happy Families</span>
          </motion.div>
          <motion.div 
            className="stat-item"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <span className="stat-number">100%</span>
            <span className="stat-label">Health Guarantee</span>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-indicator"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.6 }}
        onClick={() => scrollToSection('about')}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 13l3 3 7-7" />
            <path d="M12 17l-3-3 3-3" />
          </svg>
        </motion.div>
        <span>Scroll to explore</span>
      </motion.div>
    </section>
  )
}

export default Hero